// Sprint 2026-Jun — VoltLedger integration Phase F
// Due Date Quick Editor (module: due_date_editor). Permission-controlled
// utility: manual entry ONLY (no auto-population), bulk updates behind an
// explicit confirmation dialog, and a complete audit history (previous value,
// new value, updated by/on, reason). Writes the receivables-ext override —
// the Sales Invoice itself is NEVER mutated.
import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { toast, Toaster } from "sonner";
import {
  AlertTriangle, CalendarRange, ChevronLeft, ChevronRight, History,
  Loader2, RotateCw, Search, X,
} from "lucide-react";

const selCls = "border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const LIMIT = 50;
const SOURCE_LABELS = {
  missing: "Missing", manual_override: "Manual Override", invoice_due_date: "Invoice Due Date",
  ses_plus_days: "SES + N days", client_credit_days: "Client Credit Days",
};

export default function DueDateEditor() {
  const [filters, setFilters] = useState({ only_missing: true, client_id: "", branch_id: "", q: "" });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [newDates, setNewDates] = useState({}); // invoice_id -> date
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDate, setBulkDate] = useState("");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);

  async function load(f = filters, p = page) {
    setLoading(true);
    try {
      const params = { only_missing: f.only_missing, skip: p * LIMIT, limit: LIMIT };
      if (f.client_id) params.client_id = f.client_id;
      if (f.branch_id) params.branch_id = f.branch_id;
      if (f.q) params.q = f.q;
      const r = await api.get("/receivables/due-dates", { params });
      setData(r.data);
      setNewDates({});
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoices");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const apply = (patch = {}) => { const f = { ...filters, ...patch }; setFilters(f); setPage(0); load(f, 0); };
  const pages = Math.max(1, Math.ceil((data?.total || 0) / LIMIT));
  const rows = data?.rows || [];

  const toggleSel = (id) => setSelectedIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelectedIds((s) =>
    s.size === rows.length ? new Set() : new Set(rows.map((r) => r.invoice_id)));

  function applyBulkDate() {
    if (!bulkDate) return toast.error("Pick a date first");
    if (!selectedIds.size) return toast.error("Select at least one invoice");
    setNewDates((d) => {
      const n = { ...d };
      selectedIds.forEach((id) => { n[id] = bulkDate; });
      return n;
    });
    toast.success(`Date staged on ${selectedIds.size} invoice(s) — review then Save`);
  }

  const staged = Object.entries(newDates).filter(([, v]) => v);

  async function save() {
    setSaving(true);
    try {
      const r = await api.put("/receivables/due-dates", {
        reason: reason.trim(),
        updates: staged.map(([invoice_id, new_due_date]) => ({ invoice_id, new_due_date })),
      });
      toast.success(`${r.data.updated} due date(s) updated — audit history recorded`);
      setConfirmOpen(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="due-date-editor-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Receivables"
        title="Due Date Quick Editor"
        subtitle="Fill missing payment due dates or correct existing ones. Every change is audited (previous value, new value, who, when, why). The Sales Invoice itself is never modified."
        actions={
          <button data-testid="due-date-audit-btn" onClick={() => setAuditOpen(true)}
            className="flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-2 text-xs uppercase tracking-wider font-bold">
            <History size={13} /> Audit Log
          </button>
        }
      />

      <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 flex items-center gap-2" data-testid="due-date-warning-banner">
        <AlertTriangle size={14} className="shrink-0" />
        Manual entry only — no dates are ever auto-populated. Bulk updates require explicit confirmation and a reason.
      </div>

      <Card className="p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div className="col-span-2 relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="due-date-search" className={`${selCls} w-full pl-8`} placeholder="Invoice no / client…"
              value={filters.q} onChange={(e) => set("q", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()} />
          </div>
          <BranchFilter value={filters.branch_id} onChange={(v) => set("branch_id", v)} testid="due-date-branch" />
          <select data-testid="due-date-client" className={selCls} value={filters.client_id} onChange={(e) => set("client_id", e.target.value)}>
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="flex gap-1.5 items-center">
            <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 cursor-pointer whitespace-nowrap">
              <input data-testid="due-date-only-missing" type="checkbox" checked={filters.only_missing}
                onChange={(e) => apply({ only_missing: e.target.checked })} className="accent-orange-500" />
              Only Missing
            </label>
            <button data-testid="due-date-apply" onClick={() => apply()}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs font-bold">
              <RotateCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2 text-xs">
          <div className="text-slate-500" data-testid="due-date-counts">
            {data?.total ?? 0} invoice(s) shown · <span className="font-bold text-rose-600">{data?.missing_count ?? 0} missing due date</span>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="due-date-bulk-date" type="date" className={selCls} value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
            <button data-testid="due-date-bulk-apply" onClick={applyBulkDate}
              className="border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 px-3 py-2 text-[10px] uppercase tracking-wider font-bold">
              Stage on selected ({selectedIds.size})
            </button>
            <span className="text-slate-400">|</span>
            <span>Page {page + 1} / {pages}</span>
            <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(filters, p); }}
              className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={12} /></button>
            <button disabled={page + 1 >= pages} onClick={() => { const p = page + 1; setPage(p); load(filters, p); }}
              className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={12} /></button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="due-date-table">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-3 py-2">
                  <input type="checkbox" data-testid="due-date-select-all" className="accent-orange-500"
                    checked={rows.length > 0 && selectedIds.size === rows.length} onChange={toggleAll} />
                </th>
                {["Invoice", "Client", "Project", "Branch", "Invoice Date", "Outstanding", "Current Due Date", "Source", "New Due Date"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && !rows.length && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">
                  {filters.only_missing ? "No invoices with missing due dates 🎉" : "No invoices found."}
                </td></tr>)}
              {!loading && rows.map((r) => (
                <tr key={r.invoice_id} className={`border-b border-slate-50 ${newDates[r.invoice_id] ? "bg-orange-50/50" : ""}`}>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" data-testid={`due-date-select-${r.invoice_number}`} className="accent-orange-500"
                      checked={selectedIds.has(r.invoice_id)} onChange={() => toggleSel(r.invoice_id)} />
                  </td>
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.invoice_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.client_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap max-w-[130px] truncate" title={r.project_name}>{r.project_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.branch_name || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.invoice_date}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.outstanding)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.due_date || <span className="text-rose-600 font-bold">Missing</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold border ${r.due_source === "missing" ? "border-rose-200 bg-rose-50 text-rose-700" : r.due_source === "manual_override" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                      {SOURCE_LABELS[r.due_source] || r.due_source}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <input data-testid={`due-date-input-${r.invoice_number}`} type="date"
                      className="border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-orange-500"
                      value={newDates[r.invoice_id] || ""}
                      onChange={(e) => setNewDates((d) => ({ ...d, [r.invoice_id]: e.target.value }))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <input data-testid="due-date-reason" className="border border-slate-300 px-3 py-2 text-sm flex-1 min-w-[240px] focus:outline-none focus:border-orange-500"
            placeholder="Reason for change (required) — e.g. 'SES approved late, client confirmed revised due date'"
            value={reason} onChange={(e) => setReason(e.target.value)} />
          <button data-testid="due-date-save" onClick={() => {
            if (!staged.length) return toast.error("Stage at least one new due date first");
            if (!reason.trim()) return toast.error("A reason is required for due-date changes");
            setConfirmOpen(true);
          }}
            disabled={!staged.length}
            className="flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
            <CalendarRange size={14} /> Save {staged.length > 0 ? `(${staged.length})` : ""}
          </button>
        </div>
      </Card>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)}>
          <div data-testid="due-date-confirm-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md shadow-2xl border border-slate-200 p-5">
            <div className="text-sm font-bold text-slate-900 mb-2">Confirm bulk due-date update</div>
            <div className="text-xs text-slate-600 mb-3">
              You are about to update the payment due date on <span className="font-bold text-orange-600">{staged.length} invoice(s)</span>.
              Previous values, new values, your identity and the reason below will be permanently recorded in the audit log.
            </div>
            <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 mb-4">
              <span className="font-bold">Reason:</span> {reason}
            </div>
            <div className="flex justify-end gap-2">
              <button data-testid="due-date-confirm-cancel" onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="due-date-confirm-save" onClick={save} disabled={saving}
                className="flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
                {saving && <Loader2 size={12} className="animate-spin" />} Confirm Update
              </button>
            </div>
          </div>
        </div>
      )}

      {auditOpen && <AuditModal onClose={() => setAuditOpen(false)} />}
    </div>
  );
}

function AuditModal({ onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/receivables/due-dates/audit", { params: { limit: 300 } })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => { toast.error("Failed to load audit log"); setRows([]); });
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="due-date-audit-modal" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600">Due Date Editor</div>
            <div className="text-sm font-bold text-slate-800">Change Audit History</div>
          </div>
          <button data-testid="due-date-audit-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0">
              <tr className="bg-slate-100 text-slate-600">
                {["Invoice", "Client", "Previous", "New", "Reason", "Updated By", "Updated On"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {rows !== null && !rows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No due-date changes recorded yet.</td></tr>}
              {(rows || []).map((a) => (
                <tr key={a.id} className="border-b border-slate-50">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{a.invoice_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{a.client_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {a.previous_value || <span className="text-rose-500 font-semibold">Missing</span>}
                    <span className="text-slate-400 ml-1">({SOURCE_LABELS[a.previous_source] || a.previous_source})</span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-bold text-emerald-700">{a.new_value}</td>
                  <td className="px-3 py-2 max-w-[200px] truncate" title={a.reason}>{a.reason}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{a.updated_by}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{(a.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
